import React, { FC, useEffect, useState } from 'react';

import { SettingOutlined } from '@ant-design/icons';
import { apiErrorHandler } from '@app/services/error.service';
import { notificationService } from '@app/services/notification.service';
import { BREAKPOINTS } from '@app/style/constants';
import { setHiddenWallets } from '@FW/api/interfacePreferences/resourcesSummary';
import { useAppSelector } from '@shared/hooks/reduxHooks';
import { Container } from '@shared/styled/Container';
import { Button, Checkbox, CheckboxOptionType, Flex, Modal, Tooltip, Typography } from 'antd';
import clsx from 'clsx';
import { t } from 'i18next';
import { Controller, useForm } from 'react-hook-form';

const { Text } = Typography;

interface IResourcesSummaryConfigModalProps {
	finallyLogic: () => void;
	hiddenWallets: string[];
}

interface IHiddenWalletsFormFields {
	wallets: string[];
}

const ResourcesSummaryConfigModal: FC<IResourcesSummaryConfigModalProps> = ({
	finallyLogic,
	hiddenWallets,
}) => {
	const { transitWallets, noServiceWallets } = useAppSelector((state) => state.wallets);

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [options, setOptions] = useState<CheckboxOptionType[]>([]);

	const {
		control,
		getValues,
		handleSubmit,
		setValue,
		reset,
		formState: { isDirty },
	} = useForm<IHiddenWalletsFormFields>({});

	// Update options.
	useEffect(() => {
		if (!transitWallets || !noServiceWallets) return;

		const options = [...transitWallets, ...noServiceWallets].map(({ walletGUID, walletName }) => ({
			label: walletName,
			value: walletGUID,
		}));

		setOptions(options);
	}, [transitWallets, noServiceWallets]);

	// Update selected wallets.
	useEffect(() => {
		if (!hiddenWallets.length) return;
		setValue('wallets', hiddenWallets);
	}, [hiddenWallets]);

	// Toggle modal visibility.
	const toggleModal = () => {
		setIsModalOpen(!isModalOpen);
	};

	// Handle submit
	const onSubmit = () => {
		setIsLoading(true);
		const { wallets } = getValues();

		setHiddenWallets(wallets)
			.then(() => {
				setIsModalOpen(false);
				finallyLogic();
				reset();
				notificationService.success({ message: t('limitsPage.importView.successMessage') });
			})
			.catch((error) => apiErrorHandler(error))
			.finally(() => setIsLoading(false));
	};

	return (
		<>
			<Tooltip title={t('common.interfacePreferencesBtnTooltip')}>
				<Button onClick={toggleModal} type='text' className='text-gray'>
					<SettingOutlined style={{ fontSize: 20 }} />
				</Button>
			</Tooltip>

			<Modal
				open={isModalOpen}
				title={t('wallet.resourceSummary.settings.hideWalletsTitle')}
				onCancel={toggleModal}
				okText={t('common.save')}
				width={BREAKPOINTS.sm}
				destroyOnClose
				onOk={handleSubmit(onSubmit)}
				okButtonProps={{ loading: isLoading, disabled: !isDirty }}>
				<Flex vertical gap={8}>
					<Text className='text-gray'>{t('wallet.resourceSummary.settings.hideWalletsDesc')}</Text>
					<Text className='text-gray'>
						*{t('wallet.resourceSummary.settings.hideWalletsDesc2')}
					</Text>
					<Container className='content-wrapper'>
						<Controller
							name='wallets'
							control={control}
							render={({ field: { value: fieldValue, onChange } }) => (
								<Checkbox.Group
									style={{ gap: 8, flexDirection: 'column' }}
									onChange={onChange}
									value={fieldValue}>
									{options.map(({ value, label }, index) => {
										const isAvailable = !fieldValue?.includes(value);

										// Count how many available items we've seen so far
										const availableCount = options
											.slice(0, index + 1)
											.filter((option) => !fieldValue?.includes(option.value)).length;

										// isVisible is true only for first 3 available items
										const isVisible = isAvailable && availableCount <= 3;

										return (
											<Checkbox
												key={value}
												className={clsx(isVisible && 'text-green')}
												value={value}>
												{label}
											</Checkbox>
										);
									})}
								</Checkbox.Group>
							)}
						/>
					</Container>
				</Flex>
			</Modal>
		</>
	);
};

export default ResourcesSummaryConfigModal;
